import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import ClipLoader from "react-spinners/ClipLoader";

import LanggraphChatView from "../reusable-components/langgraphChatView";
import Layout from "../reusable-components/Layout";
import { sanitizeText } from "../utilities/sanitizeText";

function Langgraph() {
  const bottomRef = useRef(null);
  const [sending, setSending] = useState(false);
  const [allResponses, setAllResponses] = useState([]);
  const [textInput, setTextInput] = useState("");
  const [checkpointId, setCheckpointId] = useState(null);
  const [messages, setMessages] = useState([
    {
      id: 1,
      content: "Hi there, how can I help you?",
      isUser: false,
      type: "message",
    },
  ]);

  // const rag_api_url = "http://127.0.0.1:8000";
  const rag_api_url = "https://langgraph-perplexity.onrender.com";

  async function sendQuery() {
    setSending(true);
    try {
      const normalizeInput = sanitizeText(textInput);
      const key = "langgraph_prompts";
      const langgraphPrompts = JSON.parse(sessionStorage.getItem(key));

      if (langgraphPrompts?.length > 0) {
        sessionStorage.setItem(
          key,
          JSON.stringify([...langgraphPrompts, normalizeInput])
        );
      } else {
        sessionStorage.setItem(key, JSON.stringify([normalizeInput]));
      }

      // First add the user message to the chat
      const newMessageId =
        messages.length > 0
          ? Math.max(...messages.map((msg) => msg.id)) + 1
          : 1;

      // Create AI response placeholder
      const aiResponseId = newMessageId + 1;
      setMessages((prev) => [
        ...prev,
        {
          id: aiResponseId,
          content: "",
          isUser: false,
          type: "message",
          isLoading: true,
          searchInfo: {
            stages: [],
            query: "",
            urls: [],
          },
        },
      ]);

      let url = `${rag_api_url}/chat_stream/${encodeURIComponent(
        normalizeInput
      )}`;

      if (checkpointId) {
        url += `?checkpoint_id=${encodeURIComponent(checkpointId)}`;
      }

      // Connect to SSE endpoint using EventSource
      const eventSource = new EventSource(url);
      let streamedContent = "";
      let searchData = null;

      // Process incoming messages
      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === "checkpoint") {
            // Store the checkpoint ID for future requests
            setCheckpointId(data.checkpoint_id);
          } else if (data.type === "content") {
            streamedContent += data.content;

            // Update message with accumulated content
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === aiResponseId
                  ? { ...msg, content: streamedContent, isLoading: false }
                  : msg
              )
            );
          } else if (data.type === "search_start") {
            // Create search info with 'searching' stage
            const newSearchInfo = {
              stages: ["searching"],
              query: data.query,
              urls: [],
            };
            searchData = newSearchInfo;

            // Update the AI message with search info
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === aiResponseId
                  ? {
                      ...msg,
                      content: streamedContent,
                      searchInfo: newSearchInfo,
                      isLoading: false,
                    }
                  : msg
              )
            );
          } else if (data.type === "search_results") {
            try {
              // Parse URLs from search results
              const urls =
                typeof data.urls === "string"
                  ? JSON.parse(data.urls)
                  : data.urls;

              // Update search info to add 'reading' stage (don't replace 'searching')
              const newSearchInfo = {
                stages: searchData
                  ? [...searchData.stages, "reading"]
                  : ["reading"],
                query: searchData?.query || "",
                urls: urls,
              };
              searchData = newSearchInfo;

              // Update the AI message with search info
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === aiResponseId
                    ? {
                        ...msg,
                        content: streamedContent,
                        searchInfo: newSearchInfo,
                        isLoading: false,
                      }
                    : msg
                )
              );
            } catch (err) {
              console.error("Error parsing search results:", err);
            }
          } else if (data.type === "search_error") {
            // Handle search error
            const newSearchInfo = {
              stages: searchData ? [...searchData.stages, "error"] : ["error"],
              query: searchData?.query || "",
              error: data.error,
              urls: [],
            };
            searchData = newSearchInfo;

            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === aiResponseId
                  ? {
                      ...msg,
                      content: streamedContent,
                      searchInfo: newSearchInfo,
                      isLoading: false,
                    }
                  : msg
              )
            );
          } else if (data.type === "end") {
            // When stream ends, add 'writing' stage if we had search info
            if (searchData) {
              const finalSearchInfo = {
                ...searchData,
                stages: [...searchData.stages, "writing"],
              };

              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === aiResponseId
                    ? { ...msg, searchInfo: finalSearchInfo, isLoading: false }
                    : msg
                )
              );
            }

            eventSource.close();
          }
        } catch (error) {
          console.error("Error parsing event data:", error, event.data);
        }
      };

      // Handle errors
      eventSource.onerror = (error) => {
        console.error("EventSource error:", error);
        eventSource.close();

        // Only update with error if we don't have content yet
        if (!streamedContent) {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === aiResponseId
                ? {
                    ...msg,
                    content:
                      "Sorry, there was an error processing your request.",
                    isLoading: false,
                  }
                : msg
            )
          );
        }
      };

      // Listen for end event
      eventSource.addEventListener("end", () => {
        eventSource.close();
      });
    } catch (err) {
      console.log(
        err instanceof Error ? err.message : "Unknown error occurred"
      );
      setTextInput("");
    } finally {
      setSending(false);
      setTextInput("");
    }
  }

  const override = {
    display: "block",
    borderColor: "text-deepGrey",
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });

    const langgraphPrompt =
      JSON.parse(sessionStorage.getItem("langgraph_prompts")) || [];

    setAllResponses({
      prompts: langgraphPrompt,
    });
  }, [messages]);

  window.addEventListener("beforeunload", () => {
    sessionStorage.clear();
  });

  return (
    <>
      <Helmet>
        <title>Wiingr | Perplexity-LangGraph</title>
      </Helmet>
      <Layout isRag={true}>
        <div className="bg-[#181818] h-[100vh] w-full flex flex-col">
          <div className="relative w-[100%] md:w-[60%] 2xl:w-[50%] h-full mx-auto flex flex-col">
            <div
              className="w-full flex py-2 tracking-wider justify-center bg-clip-text text-transparent 
  bg-gradient-to-r from-white/60 via-white/10 to-white/60
  bg-[length:200%_200%] animate-gradient-move"
            >
              <p className="font-saira font-light text-xs sm:text-sm uppercase">
                LangGraph-Powered Perplexity ChatBot
              </p>
            </div>
            <div className="relative h-full flex flex-col justify-end overflow-hidden">
              <LanggraphChatView
                messages={messages}
                allResponses={allResponses}
                bottomRef={bottomRef}
              />
              <div className="mb-10 flex justify-center mx-3">
                <div className="bg-[rgba(0,0,0,10%)] w-full h-fit rounded-xl flex flex-row items-center">
                  <textarea
                    type="text"
                    placeholder="Ask Anything..."
                    row={10}
                    value={textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                    className="resize-none w-[100%] mt-3 mb-[4px] pl-5 border-[0.5px] focus:ring-0 focus:border-none border-none rounded-xl bg-transparent text-sm leading-5 text-white/40 placeholder:text-white/40"
                  />
                  <div className="flex flex-row justify-between items-center px-3 pb-3 mr-2">
                    {sending ? (
                      <ClipLoader
                        color="text-lightGrey"
                        loading={sending}
                        cssOverride={override}
                        size={20}
                        aria-label="Loading Spinner"
                        data-testid="loader"
                      />
                    ) : (
                      <button
                        disabled={sending || !textInput}
                        onClick={sendQuery}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className="size-4 text-white/40 hover:text-white/20 cursor-pointer transition-all duration-500"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M4.5 10.5 12 3m0 0 7.5 7.5M12 3v18"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    </>
  );
}

export default Langgraph;
